<template>
  <div class="container-fluid order-reference-index-container">
    <div class="card">
      <div class="card-header">
        <div class="row">
          <div class="card-header-label col-2">
            <span class="mdi mdi-clipboard-outline custom-icon"></span>
            <span class="font-weight-bold">Órdenes de producción</span>
          </div>
          <div class="card-header-search-bar col-4">
            <b-form-input
              v-model="filter"
              id="filter-input"
              placeholder="Escriba aquí el texto a buscar"
            />
          </div>
          <div class="card-header-pagination col-4">
            <b-pagination 
              v-model="currentPage" 
              :total-rows="filteredList ? filteredList.length : 0" 
              :per-page="perPage"  
              aria-controls="list-table"
              style="margin: 0"
              align="center" 
            />
          </div>
          <div class="card-header-action-buttons gap-2 col-2">
            <ButtonComponent
              :buttonText="'Filtros'"
              :buttonColor="colors.GRAY_COLOR_BUTTON"
              :buttonAction="openFilterModal"
            />
            <StockOrderCreateButton
              @refreshList="getList"
            />
            <ButtonComponent
              :buttonText="'Refrescar'"
              :buttonColor="colors.BLUE_COLOR_BUTTON"
              :buttonAction="refreshList"
            />
          </div>
        </div>
      </div>
      <div v-if="isLoading" style="padding: 5ch 0;">
        <SpeenComponent 
          :isAutomatic="true" 
          :ringWidth="14" 
          :spinnerSize="15" 
          :isverticalAligned="true" 
        />
      </div>
      <div class="card-body" v-else>
        <b-table 
          :items="filteredList" 
          :fields="fields" 
          small striped hover 
          :per-page="perPage"
          :current-page="currentPage" 
          id="list-table"
        >
          <template #cell(orderNo)="data">
            <div @click="copyToClipboard(data.item.id)" style="cursor: pointer;" title="Copiar ID al portapapeles" :class="getOrderNoTagClass(data.item)">
              {{ data.item.orderNo }}
            </div>
            <div :class="getStateTagClass(data.item.orderStateValue)">
              {{ data.item.stateText }}
            </div>
          </template>

          <template #cell(cargoDate)="data">
            <div>{{ parseStrDateTimeToSpanishStrDate(data.item.cargoDate) }}</div>
            <div :class="getCargoDateTagClass(data.item)">
              {{ getCargoDateText(data.item) }}
            </div>
          </template>              
          
          <template #cell(clientBusinessName)="data">
            <div>{{ data.item.clientBusinessName }}</div>
            <div class="tag tag-light-blue">{{ data.item.clientName }}</div>
          </template>

          <template #cell(referenceDescrip)="data">
            <div>{{ data.item.referenceDescrip }}</div>
            <div class="tags-container">
              <div class="tag tag-light-pink">{{ data.item.referenceCode }}</div>
              <div class="tag tag-light-green">{{ data.item.variety }}</div>
              <div class="tag tag-light-blue">{{ data.item.batch }}</div>
            </div>
          </template>

          <template #cell(realQuantity)="data">
            <div>{{ formatCantidad(data.item) }}</div>
            <div class="tag tag-light-pink">{{ productLineText(data.item) }} </div>
          </template>

          <template #cell(actions)="data">
            <ButtonComponent
              v-if="isComputedState(data.item.orderStateValue)"
              :buttonText="'Ver'"
              :buttonColor="colors.BLUE_COLOR_BUTTON"
              :buttonAction="() => viewItem(data.item)"
            />
            <ButtonComponent
              v-if="canDeleteStockOrderReference(data.item)"
              :buttonText="'Borrar'"
              :buttonColor="colors.RED_COLOR_BUTTON"
              :buttonAction="() => confirmDeleteItem(data.item)"
            />
          </template>
        </b-table>
      </div>
    </div>
    <order-reference-index-filter
      v-if="showFilterModal"
      :filtersProp="filters"
      :applyFiltersProp="applyFiltersHandler"
      :loadFiltersProp="loadFilters"
      @closeFilterModal="closeFilterModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import SpeenComponent from '../../common/components/SpeenComponent.vue';
import { showCustomErrorSweetAlert, showCustomSuccessSweetAlert } from '../../common/utils';
import OrderStatesFinal from '../../common/enums/order_states_final';
import OrderReferenceIndexFilter from './OrderReferenceIndexFilter.vue';
import StockOrderCreateButton from '../components/StockOrderCreateButton.vue';
import ButtonComponent from '../../common/components/ButtonComponent.vue';
import Colors from '../../common/colors';
import { mapGetters } from 'vuex';
import PermissionsConsts from '../../common/permissions';

export default {
  name: 'OrderReferenceIndexComponent',
  components: {
    SpeenComponent,
    OrderReferenceIndexFilter,
    StockOrderCreateButton,
    ButtonComponent
  },
  data() {
    return {
      extraSearchKeys: ['clientName', 'referenceCode', 'variety', 'batch', 'stateText', 'lineNoText'],
      perPage: 10,
      currentPage: 1,
      filter: '',
      listData: [],
      isLoading: true,
      showCreateModal: false,
      showEditModal: false,
      currentItemId: -1,
      showFilterModal: false,
      filters: {
        cargo_date_from: "",
        cargo_date_to: "",
        client_name: "",
        shipping_address: "",
        reference_id: -1,
        orderName: "",
        varietyName: "",
        batchName: "",
        production_lines: [],
        showStPending: true,
        showStQueue: true,
        showStProd: true,
        showStFinished: false,
        showOnlyStockOrders: false,
        showOnlyNonStockOrders: false
      },
      colors: Colors,
      fields: [
        { key: 'orderNo',             label: 'Orden',           sortable: true,   class: 'truncate' },
        { key: 'cargoDate',           label: 'Fecha de carga',  sortable: true,   class: 'truncate' },
        { key: 'clientBusinessName',  label: 'Cliente',         sortable: true,   class: 'truncate' },
        { key: 'referenceDescrip',    label: 'Artículo',        sortable: true,   class: 'truncate' },
        { key: 'realQuantity',        label: 'Cantidad',        sortable: true,   class: 'truncate' },
        { key: 'actions',             label: 'Acción',          sortable: false,  class: 'truncate' }
      ],
    };
  },
  computed: {
    ...mapGetters(['userPermissions', 'userHasPermission']),
    filteredList() {
      if (!this.filter) {
        return this.listData;
      }
      var searchTerm = this.filter.toLowerCase();
      var searchKeys = this.getSearchKeys();
      var result = [];
      for (var i = 0; i < this.listData.length; i++) {
        var currentItem = this.listData[i];
        if (this.listMatchesSearch(currentItem, searchKeys, searchTerm)) {
          result.push(currentItem);
        }
      }
      return result;
    },
    userCanDeleteOrderReference() {
      return this.userHasPermission(PermissionsConsts.STOCK_ORDER_REFERENCE_DELETE);
    },
  },
  methods: {
    initFilterValues() {
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      const future = new Date(today);
      future.setDate(today.getDate() + 7);
      this.filters = {
      ...this.filters,
      cargo_date_from: this.formatDate(past),
      cargo_date_to: this.formatDate(future),
      client_name: "",
      shipping_address: "",
      reference_id: -1,
      orderName: "",
      varietyName: "",
      batchName: "",
      production_lines: [],
      showStPending: true,
      showStQueue: true,
      showStProd: true,
      showStFinished: false,
      showOnlyStockOrders: false,
      showOnlyNonStockOrders: false
      };
    },
    loadFilters() {
      const filters = sessionStorage.getItem('filters');
      if (filters) {
        const parsedFilters = JSON.parse(filters);
        this.filters = {
          cargo_date_from: parsedFilters.cargo_date_from,
          cargo_date_to: parsedFilters.cargo_date_to,
          client_name: parsedFilters.client_name,
          shipping_address: parsedFilters.shipping_address,
          reference_id: parsedFilters.reference_id,
          orderName: parsedFilters.orderName,
          varietyName: parsedFilters.varietyName,
          batchName: parsedFilters.batchName,
          production_lines: parsedFilters.production_lines,
          showStPending: parsedFilters.showStPending,
          showStQueue: parsedFilters.showStQueue,
          showStProd: parsedFilters.showStProd,
          showStFinished: parsedFilters.showStFinished,
          showOnlyStockOrders: parsedFilters.showOnlyStockOrders,
          showOnlyNonStockOrders: parsedFilters.showOnlyNonStockOrders
        };
      }
    },
    getSearchKeys: function() {
      var keys = [];
      for (var i = 0; i < this.fields.length; i++) {
        var field = this.fields[i];
        if (field.key !== 'actions') {
          keys.push(field.key);
        }
      }
      if (this.extraSearchKeys && this.extraSearchKeys.length) {
        keys = keys.concat(this.extraSearchKeys);
      }
      return keys;
    },
    listMatchesSearch: function(currentItem, searchKeys, searchTerm) {
      for (var i = 0; i < searchKeys.length; i++) {
        var key = searchKeys[i];
        var value = currentItem[key];
        if (this.valueMatchesSearch(value, searchTerm)) {
          return true;
        }
      }
      return false;
    },
    valueMatchesSearch: function(value, searchTerm) {
      if (typeof value === 'object' && value !== null) {
        for (var key in value) {
          if (value.hasOwnProperty(key)) {
            if (String(value[key]).toLowerCase().indexOf(searchTerm) !== -1) {
              return true;
            }
          }
        }
        return false;
      } else {
        return String(value).toLowerCase().indexOf(searchTerm) !== -1;
      }
    },
    getList() {
      this.isLoading = true;
      const jsonBody = JSON.stringify({ ...this.filters });
      axios.post('/api/order-reference/seek', jsonBody, {
        headers: { 'Content-Type': 'application/json' }
      })
        .then(response => {
          this.listData = response.data;
          this.isLoading = false;
        })
        .catch(error => {
          showCustomErrorSweetAlert(error.response.data);
          this.isLoading = false;
        });
    },
    refreshList() {
      this.getList();
    },
    openCreateModal() {
      this.showCreateModal = true;
    },
    openEditModal(clickedItem) {
      this.currentItemId = clickedItem.id;
      this.showEditModal = true;
    },
    viewItem(item) {
      window.location.href = `order-reference-view/${item.id}`;
    },
    confirmDeleteItem(item) {
      Swal.fire({
        title: 'UMA',
        text: `¿Está seguro de eliminar la orden contra stock "${item.orderNo}" ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'red',
        cancelButtonColor: 'gray',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteItem(item);
        }
      });
    },
    deleteItem(item) {
      axios.delete(`/api/order-line/${item.id}`)
        .then(() => {
          showCustomSuccessSweetAlert('Orden de producción eliminada correctamente');
          this.getList();
        })
        .catch(error => {
          showCustomErrorSweetAlert(error.response.data);
        })
        .finally(() => {
          this.refreshList();
        });
    },
    handleItemCreated() {
      this.getList();
      this.showCreateModal = false;
    },
    handleItemEdited() {
      this.getList();
      this.showEditModal = false;
    },
    formatCantidad(item) {
      return item.producedQuantity + ' / ' + item.realQuantity;
    },
    getStateTagClass(stateId) {
      if (stateId === OrderStatesFinal.PENDING) return 'tag tag-light-pink';
      if (stateId === OrderStatesFinal.QUEUED) return 'tag tag-light-yellow';
      if (stateId === OrderStatesFinal.IN_PRODUCTION) return 'tag tag-light-blue';
      if (stateId === OrderStatesFinal.FINISHED) return 'tag tag-light-green';
      return 'tag';
    },
    getOrderNoTagClass(item) {
      let className = '';
      if(item.isStock) className += 'stock-order ';
      return className;
    },
    getCargoDateTagClass(item) {
      const endDate = this.parseStrDateTimeToDateWithoutTime(item.endDate, "2000-01-01");
      const cargoDate = this.parseStrDateTimeToDateWithoutTime(item.cargoDate, "2000-01-01");
  
      if (item.orderStateValue === OrderStatesFinal.PENDING) return 'tag tag-light-blue';
      if (endDate > cargoDate) {
        return 'tag tag-light-pink';
      }
      return 'tag tag-light-green';
    },
    getCargoDateText(item) {
      if (item.orderStateValue === OrderStatesFinal.PENDING) {
        return "Finalización no prevista";
      }
      return "Inicio: " + this.parseStrDateTimeToSpanishStrDateTime(item.startDate) +
             " Fin: " + this.parseStrDateTimeToSpanishStrDateTime(item.endDate);
    },
    isComputedState(stateValue) {
      return stateValue === OrderStatesFinal.IN_PRODUCTION || stateValue === OrderStatesFinal.FINISHED;
    },
    isStockOrderReference(item) {
      return item.isStock;
    },
    canDeleteStockOrderReference(item) {
      return this.userCanDeleteOrderReference && this.isPending(item) && this.isStockOrderReference(item);
    },
    isPending(item) {
      return item.orderStateValue === OrderStatesFinal.PENDING || item.orderStateValue === OrderStatesFinal.QUEUED;
    },
    productLineText(item) {
      return item.lineNoText;
    },
    parseStrDateTimeToDateWithoutTime(dateStr, nullValue) {
      if (dateStr === "") {
        return nullValue;
      }
      const datePart = dateStr.split(' ')[0];
      return new Date(datePart);
    },
    parseStrDateTimeToSpanishStrDate(dateStr) {
      if (dateStr === "") {
        return "";
      }
      const [datePart] = dateStr.split(' ');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    },
    formatDate(d) {
      const year = d.getFullYear();
      let month = d.getMonth() + 1;
      let day = d.getDate();
      if (month < 10) month = "0" + month;
      if (day < 10) day = "0" + day;
      return `${year}-${month}-${day}`;
    },
    parseStrDateTimeToSpanishStrDateTime(dateTimeStr) {
      if (dateTimeStr === "") {
        return "";
      }
      const [datePart, timePart] = dateTimeStr.split(' ');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year} ${timePart}`;
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).catch(err => {
        console.error('Error al copiar el texto: ', err);
      });
    },
    applyFiltersHandler(filters)
    {
      this.filters.cargo_date_from = filters.cargo_date_from;
      this.filters.cargo_date_to = filters.cargo_date_to;
      this.filters.client_name = filters.client_name;
      this.filters.shipping_address = filters.shipping_address;
      this.filters.reference_id = filters.reference_id;
      this.filters.orderName = filters.orderName;
      this.filters.varietyName = filters.varietyName;
      this.filters.batchName = filters.batchName;
      this.filters.production_lines = filters.production_lines;
      this.filters.showStPending = filters.showStPending;
      this.filters.showStQueue = filters.showStQueue;
      this.filters.showStProd = filters.showStProd;
      this.filters.showStFinished = filters.showStFinished;
      this.filters.showOnlyStockOrders = filters.showOnlyStockOrders;
      this.filters.showOnlyNonStockOrders = filters.showOnlyNonStockOrders;
      this.getList();
    },
    closeFilterModal() {
      this.showFilterModal = false;
    },
    openFilterModal() {
      this.showFilterModal = true;
    },

  },
  mounted() {
    this.initFilterValues();
    this.loadFilters();
    this.getList();
  }
};
</script>

<style lang="scss">
.order-reference-index-container {
  #list-table table {
    table-layout: fixed;
  }
  #list-table {
    [aria-colindex="1"] { width: 12%; }
    [aria-colindex="2"] { width: 24%; }
    [aria-colindex="3"] { width: 27%; }
    [aria-colindex="4"] { width: 23%; }
    [aria-colindex="5"] { width: 10%; }
    [aria-colindex="6"] { width: 8%; }
  }
  .tag {
    font-size: 0.8rem;
    border: 1px solid #e0e0e0;
    padding: 1px 2px;
    border-radius: 4px;
    display: inline-block;
    margin-top: 4px;
  }
  .tag-light-pink {
    background-color: #fce4ec;
    border-color: #f8bbd0;
    margin-right: 4px;
  }
  .tag-light-green {
    background-color: #e8f5e9;
    border-color: #c8e6c9;
    margin-right: 4px;
  }
  .tag-light-blue {
    background-color: #e3f2fd;
    border-color: #bbdefb;
  }
  .custom-icon {
    font-size: 1.8rem;
    margin-right: 10px;
  }
  .tag-light-yellow {
    background-color: #fffde7;
    border-color: #fff9c4;
    margin-right: 4px;
  }
  .custom-header {
    display: flex;
    align-items: center;
    margin-left: 20px; 
  }
  // .card-header {
  //   margin-top: 0;
  //   margin-bottom: 0;
  //   padding-top: 0;
  //   padding-bottom: 0;
  //   height: 60px;
  //   line-height: 16px;
  //   display: flex;
  //   align-items: center;
  //   &>div {
  //     width: 100%;
  //   }
  // }
  
  .pagination-container-center {
    &>ul {
      margin: 0;
    }
  }
  
  .stock-order {
    background-color: #e3f2fd;
    border:1px solid #bbdefb;
    color: #435ad1;
    font-weight: bold;
    padding: 0 0.5ch;
  }
}
</style>
